import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  ordersUpdateFulfillmentOrderShippingAddressMutation,
  type ChangeFulfillmentOrderShippingAddressResponseRoot,
} from '@repo/sdk'

import { extractUserFacingMessage } from '../../providers/sdkErrorUtils'

import { useInvalidateFulfillmentOrders } from './useInvalidateFulfillmentOrders'

interface ChangeShippingAddressParams {
  fulfillmentOrderId: string
  addressId: string
  reason?: string
  onDone: () => void
}

/** #87: move the parcel to another of the buyer's registered addresses (dealer variant). */
export function useChangeShippingAddress() {
  const invalidateFulfillmentOrders = useInvalidateFulfillmentOrders()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutate, isPending } = useMutation({
    ...ordersUpdateFulfillmentOrderShippingAddressMutation(),
    onSuccess: async (data: ChangeFulfillmentOrderShippingAddressResponseRoot) => {
      const movedCount = data.changedFulfillmentOrderIds.length
      message.success(
        movedCount > 1
          ? t('pages.orders.changeShippingAddress.successGroup', { count: movedCount })
          : t('pages.orders.changeShippingAddress.success'),
      )
      // The whole parcel may have moved: refresh every fulfillment-order view, not just this id.
      await invalidateFulfillmentOrders()
    },
    onError: (error) => {
      message.error(
        extractUserFacingMessage(error) ?? t('pages.orders.changeShippingAddress.error'),
      )
    },
  })

  const changeShippingAddress = ({
    fulfillmentOrderId,
    addressId,
    reason,
    onDone,
  }: ChangeShippingAddressParams) => {
    mutate(
      {
        path: { fulfillmentOrderId },
        body: { addressId, ...(reason ? { reason } : {}) },
      },
      { onSuccess: onDone },
    )
  }

  return { changeShippingAddress, isChanging: isPending }
}
